/**
 * API Service
 *
 * Centralised axios client with refresh-token-aware interceptors.
 *
 * Two parallel sessions are supported (regular user + admin) backed by
 * separate localStorage keys so an operator can hold both at once:
 *
 *   user:   token / refresh_token / user
 *   admin:  admin_token / admin_refresh_token / admin_user
 *
 * On a 401 response the interceptor tries `/auth/refresh` ONCE with the
 * matching refresh token. On success the new tokens are stored and the
 * original request is replayed. On failure the relevant session is cleared
 * and the error is propagated so the UI can redirect to login.
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});

// ─── Session helpers ────────────────────────────────────────────────────────
// Centralised so storage key names live in one place.

function sessionKindFor(url) {
  if (!url) return 'user';
  if (url.startsWith('/admin')) return 'admin';
  if (url.includes('/auth/admin-')) return 'admin';
  return 'user';
}

function tokenKey(kind)   { return kind === 'admin' ? 'admin_token'         : 'token'; }
function refreshKey(kind) { return kind === 'admin' ? 'admin_refresh_token' : 'refresh_token'; }
function userKey(kind)    { return kind === 'admin' ? 'admin_user'          : 'user'; }

export function getAccessToken(kind)  { return localStorage.getItem(tokenKey(kind)); }
export function getRefreshToken(kind) { return localStorage.getItem(refreshKey(kind)); }

export function storeSession(kind, { access_token, refresh_token, user }) {
  if (access_token)  localStorage.setItem(tokenKey(kind),   access_token);
  if (refresh_token) localStorage.setItem(refreshKey(kind), refresh_token);
  if (user)          localStorage.setItem(userKey(kind),    JSON.stringify(user));
}

export function clearSession(kind) {
  localStorage.removeItem(tokenKey(kind));
  localStorage.removeItem(refreshKey(kind));
  localStorage.removeItem(userKey(kind));
}

// ─── Request interceptor: attach the right access token ────────────────────
apiClient.interceptors.request.use(
  (config) => {
    const kind = sessionKindFor(config.url || '');
    const token = getAccessToken(kind);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Refresh queue: collapse concurrent 401s into a single refresh call ────
// Multiple in-flight requests hitting 401 at the same time should not each
// trigger their own /auth/refresh — that would make the rotation chain
// invalidate itself the moment the second one lands.
const refreshState = { user: null, admin: null }; // promises, keyed by kind

async function refreshTokensFor(kind) {
  if (refreshState[kind]) return refreshState[kind];

  const refresh_token = getRefreshToken(kind);
  if (!refresh_token) {
    return Promise.reject(new Error('No refresh token'));
  }

  // Use a *bare* axios call to skip our interceptors (which would re-attach
  // an expired access token and loop).
  refreshState[kind] = axios
    .post(`${API_URL}/auth/refresh`, {
      refresh_token,
      source: kind === 'admin' ? 'admin' : 'web',
    }, { headers: { 'Content-Type': 'application/json' } })
    .then((res) => {
      const payload = res.data && res.data.data ? res.data.data : res.data;
      const access_token  = payload?.access_token;
      const new_refresh   = payload?.refresh_token;
      if (!access_token || !new_refresh) throw new Error('Refresh response missing tokens');
      storeSession(kind, { access_token, refresh_token: new_refresh });
      return access_token;
    })
    .catch((err) => {
      clearSession(kind);
      throw err;
    })
    .finally(() => { refreshState[kind] = null; });

  return refreshState[kind];
}

// ─── Response interceptor: refresh-on-401, then retry once ─────────────────
apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const original = error.config || {};
    const status = error.response && error.response.status;

    // Try a refresh on a 401, but only once per original request and never
    // for the refresh endpoint itself.
    const isRefreshCall = (original.url || '').includes('/auth/refresh');
    if (status === 401 && !original._retried && !isRefreshCall) {
      const kind = sessionKindFor(original.url || '');
      try {
        const newAccess = await refreshTokensFor(kind);
        original._retried = true;
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newAccess}` };
        return apiClient.request(original);
      } catch (_refreshErr) {
        // fall through to the normal error path with the original 401
      }
    }

    if (error.response) {
      const errorMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        'An error occurred';

      // Surface 401 without a usable refresh: wipe the matching session so
      // the UI's "is authenticated?" check flips false.
      if (status === 401 && !isRefreshCall) {
        clearSession(sessionKindFor(original.url || ''));
      }

      const err = new Error(errorMessage);
      err.response = { status, data: error.response.data };
      return Promise.reject(err);
    }
    return Promise.reject(error);
  }
);

// ─── Auth API ──────────────────────────────────────────────────────────────
export const authAPI = {
  login:               (credentials) => apiClient.post('/auth/login', credentials),
  register:            (userData)    => apiClient.post('/auth/register', userData),
  forgotPassword:      (email)       => apiClient.post('/auth/forgot-password', { email }),
  resetPassword:       (token, password) => apiClient.post('/auth/reset-password', { token, password }),
  adminLogin:          (credentials) => apiClient.post('/auth/admin-login', credentials),
  adminVerifyOtp:      (payload)     => apiClient.post('/auth/admin-verify-otp', payload),
  requestEmailChange:  (payload)     => apiClient.post('/auth/request-email-change', payload),
  resendEmailChange:   ()            => apiClient.post('/auth/resend-email-change', {}),
  verifyEmailChange:   (otp)         => apiClient.post('/auth/verify-email-change', { otp }),
  changePassword:      (payload)     => apiClient.post('/auth/change-password', payload),
  verifyEmail:         (token)       => apiClient.post('/auth/verify-email', { token }),
  resendVerification:  (email)       => apiClient.post('/auth/resend-verification', { email }),

  // Server-side revoke of the user session refresh token, then local wipe.
  // Safe to call even when the refresh token is already expired/missing.
  logout: async () => {
    const refresh_token = getRefreshToken('user');
    if (refresh_token) {
      try { await apiClient.post('/auth/logout', { refresh_token }); } catch { /* ignore */ }
    }
    clearSession('user');
  },
};

export const transcriptionsAPI = {
  list:   ()     => apiClient.get('/transcriptions'),
  create: (data) => apiClient.post('/transcriptions', data),
};

export const ordersAPI = {
  list: () => apiClient.get('/orders'),
};

export const plansAPI = {
  list:    ()      => apiClient.get('/plans'),
  listAll: ()      => apiClient.get('/plans?all=1'),
  get:     (id)    => apiClient.get(`/plans/${id}`),
  create:  (data)  => apiClient.post('/plans', data),
  update:  (id, data) => apiClient.put(`/plans/${id}`, data),
  remove:  (id)    => apiClient.delete(`/plans/${id}`),
};

export const adminAPI = {
  listUsers:  (search) => apiClient.get('/admin/users', { params: search ? { search } : {} }),
  updateUser: (id, data) => apiClient.put(`/admin/users?id=${id}`, data),
  deleteUser: (id)     => apiClient.delete(`/admin/users?id=${id}`),
  listOrders: (status) => apiClient.get('/admin/orders', { params: status ? { status } : {} }),
  getStats:   ()       => apiClient.get('/admin/stats'),

  // Admin equivalent of authAPI.logout — revokes the admin refresh token.
  logout: async () => {
    const refresh_token = getRefreshToken('admin');
    if (refresh_token) {
      try {
        await apiClient.post('/auth/logout', { refresh_token });
      } catch { /* ignore */ }
    }
    clearSession('admin');
  },
};

export default apiClient;
